/*
eWorldES Proprietary Software
Copyright (C) 2025 eWorldES. All rights reserved.
 
This software is proprietary to eWorldES and is protected by copyright laws and international treaties.
You may not use, copy, modify, distribute, or disclose this software without explicit written permission from eWorldES.
Any unauthorized use or reproduction of this software is strictly prohibited and may result in legal action.
 
License Agreement:
By using this software, you agree to be bound by the terms of the eWorldES Proprietary Software License Agreement.
For a copy of the agreement, visit https://eworldes.com/ or contact eWorldES directly for more information.
 
This software is provided "AS IS" without warranty of any kind, express or implied, including but not limited to the warranties of
merchantability, fitness for a particular purpose, or non-infringement. In no event shall eWorldES be liable for any damages
arising out of the use of this software, even if eWorldES has been advised of the possibility of such damages.
 
For inquiries or requests regarding this software, please contact eWorldES at:
Email: info@eworldes.com
Website: https://eworldes.com/
*/

import React, { useState, useEffect, useCallback } from "react";
import {
    Avatar,
    Box,
    Paper,
    Stack,
    Typography,
    Tooltip,
    useMediaQuery,
    TextField,
    useTheme,
    Divider,
} from "eworldes-ui-toolkit-v1.1.0";
import { styled } from "@mui/material/styles";
import SmartToyIcon from "@mui/icons-material/SmartToy";
import SendIcon from "@mui/icons-material/Send";
import ChatIcon from "@mui/icons-material/Chat";
import FullscreenIcon from "@mui/icons-material/Fullscreen";
import FullscreenExitIcon from "@mui/icons-material/FullscreenExit";
import CloseIcon from "@mui/icons-material/Close";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import { IconButton } from "../BaseComponents/BaseIcons";
import { Messages } from "./Message";
import { CHATBOT_CONFIG } from "config/chatbotConfig";
import colors from "theme/colors";
import axios from "axios";
import { API_BASE_URL } from "config/appConfig";

const INTRO_MSG = "Hello! How can I assist you today? Just type your question.";

const ChatWrapper = styled("div")<{ isFullscreen: boolean; isOpen: boolean }>(
    ({ theme, isFullscreen, isOpen }) => ({
        padding: theme.spacing(1),
        zIndex: 1000,
        position: "fixed",
        bottom: 20,
        right: 20,
        transition: "all 0.3s ease-in-out",
        opacity: isOpen ? 1 : 0,
        visibility: isOpen ? "visible" : "hidden",
        transform: isOpen ? "translateY(0)" : "translateY(20px)",
        [theme.breakpoints.up("xs")]: {
            width: isFullscreen ? "calc(100% - 40px)" : "400px",
        },
        [theme.breakpoints.down("md")]: {
            width: isFullscreen ? "calc(100% - 40px)" : "320px",
        },
        [theme.breakpoints.up("md")]: {
            width: isFullscreen ? "calc(100% - 200px)" : "500px",
        },
    })
);

const LoaderDots = styled("div")(() => ({
    width: "60px",
    aspectRatio: "4",
    background:
        "radial-gradient(circle closest-side, #000 90%, #0000) 0/calc(100%/3) 100% space",
    clipPath: "inset(0 100% 0 0)",
    animation: "l1 1s steps(4) infinite",
    "@keyframes l1": {
        to: {
            clipPath: "inset(0 -34% 0 0)",
        },
    },
}));

// Styled floating button to replace Fab and avoid MUI import
const FloatButton = styled("button")(() => ({
    position: "fixed",
    bottom: 25,
    right: 25,
    backgroundColor: colors.lco.primary.main,
    color: colors.lco.white,
    border: "none",
    borderRadius: "28px",
    padding: "12px 24px",
    display: "flex",
    alignItems: "center",
    cursor: "pointer",
    boxShadow: "0 4px 10px rgba(0,0,0,0.2)",
    fontSize: "0.875rem",
    fontWeight: 600,
    textTransform: "uppercase",
    letterSpacing: "0.02857em",
    zIndex: 1000,
    transition: "background-color 0.2s ease, box-shadow 0.2s ease",
    backfaceVisibility: "hidden",
    transform: "translateZ(0)",
    "&:hover": {
        backgroundColor: "#8E24AA", // Slightly darker purple, not the deep secondary one
        boxShadow: "0 6px 15px rgba(0,0,0,0.25)",
    },
    "&:active": {
        backgroundColor: "#7B1FA2",
    },
}));

export const ChatBot = () => {
    const theme = useTheme();

    const [userInput, setUserInput] = useState("");
    const [sessionId, setSessionId] = useState("");
    const [typingIndicator, setTypingIndicator] = useState(false);
    const [messages, setMessages] = useState<any[]>([
        {
            type: "ai",
            text: INTRO_MSG,
        },
    ]);
    const [isChatOpen, setIsChatOpen] = useState(false);
    const [isFullScreen, setfullScreen] = useState(false);

    const isSmallDevice = useMediaQuery(theme.breakpoints.down("sm"));
    const isMediumDevice = useMediaQuery(theme.breakpoints.down("md"));

    const generateSession = useCallback(() => {
        const newSessionId = Math.random().toString(36).substring(2, 15);
        setSessionId(newSessionId);
    }, []);

    useEffect(() => {
        generateSession();
    }, [generateSession]);

    const restartSession = () => {
        generateSession();
        setMessages([
            {
                type: "ai",
                text: INTRO_MSG,
            },
        ]);
    };

    const toggleFullScreen = () => {
        setfullScreen((prev) => !prev);
    };

    const handleUserInput = (e: React.ChangeEvent<HTMLInputElement>) => {
        setUserInput(e.target.value);
    };

    const sendMessage = async () => {
        const trimmedInput = userInput.trim();
        if (!trimmedInput || typingIndicator) return;

        try {
            setTypingIndicator(true);
            setMessages((prev) => [
                ...prev,
                {
                    text: trimmedInput,
                    type: "user",
                },
            ]);
            setUserInput("");

            const payload = {
                prompt: trimmedInput,
                userId: sessionId,
                sessionId: sessionId,
            };

            const response = await axios.post(`${API_BASE_URL}${CHATBOT_CONFIG.apiEndpoint}`, payload, {
                headers: {
                    "Content-Type": "application/json",
                },
            });

            const data = response.data;
            setTypingIndicator(false);

            let aiText = data.completion || data.response || data.text || data;
            if (typeof aiText === "object" && aiText !== null) {
                aiText =
                    aiText.completion ||
                    aiText.response ||
                    aiText.text ||
                    JSON.stringify(aiText);
            }

            setMessages((prev) => [
                ...prev,
                {
                    text: String(aiText),
                    type: "ai",
                },
            ]);
        } catch (error) {
            console.error("ChatBot Error:", error);
            setTypingIndicator(false);
            setMessages((prev) => [
                ...prev,
                {
                    text: "I'm having trouble connecting right now. Please try again in a moment.",
                    type: "system",
                    error: true,
                },
            ]);
        }
    };

    return (
        <>
            <ChatWrapper
                isFullscreen={isFullScreen}
                isOpen={isChatOpen}
                sx={{
                    height: isSmallDevice ? "80vh" : isMediumDevice ? "80vh" : "85vh",
                }}
            >
                <Paper
                    elevation={3}
                    sx={{
                        borderRadius: "20px",
                        width: "100%",
                        height: "100%",
                        position: "relative",
                        outline: `1px solid ${colors.lco.divider}`,
                        boxShadow: "rgba(149, 157, 165, 0.2) 0px 8px 24px",
                        overflow: "hidden",
                        display: "flex",
                        flexDirection: "column",
                    }}
                >
                    {/* Header */}
                    <Box
                        sx={{
                            bgcolor: colors.lco.primary.main,
                            color: colors.lco.white,
                            px: 2,
                            py: 1,
                            display: "flex",
                            alignItems: "center",
                            height: "70px",
                        }}
                    >
                        <Typography
                            variant="h6"
                            sx={{
                                ml: 1,
                                fontSize: "1.05rem",
                                fontWeight: 600,
                                flexGrow: 1,
                                color: "inherit",
                            }}
                        >
                            {CHATBOT_CONFIG.chatTitle}
                        </Typography>
                        <Stack direction="row" spacing={0.5}>
                            <IconButton
                                onClick={toggleFullScreen}
                                sx={{ color: "inherit", p: 0.5 }}
                            >
                                <Tooltip title={isFullScreen ? "Exit Fullscreen" : "Fullscreen"}>
                                    <Box sx={{ display: "flex" }}>
                                        {isFullScreen ? <FullscreenExitIcon /> : <FullscreenIcon />}
                                    </Box>
                                </Tooltip>
                            </IconButton>

                            <IconButton
                                onClick={restartSession}
                                sx={{ color: "inherit", p: 0.5 }}
                            >
                                <Tooltip title="Restart Conversation">
                                    <Box sx={{ display: "flex" }}>
                                        <RestartAltIcon />
                                    </Box>
                                </Tooltip>
                            </IconButton>

                            <IconButton
                                onClick={() => setIsChatOpen(false)}
                                sx={{ color: "inherit", p: 0.5 }}
                            >
                                <Tooltip title="Hide Chat">
                                    <Box sx={{ display: "flex" }}>
                                        <CloseIcon />
                                    </Box>
                                </Tooltip>
                            </IconButton>
                        </Stack>
                    </Box>

                    {/* Messages Area */}
                    <Box
                        sx={{
                            flexGrow: 1,
                            overflowY: "auto",
                            p: 2,
                            bgcolor: colors.lco.background.default,
                            display: "flex",
                            flexDirection: "column",
                        }}
                    >
                        <Messages messages={messages} />

                        {typingIndicator && (
                            <Stack
                                direction="row"
                                alignItems="center"
                                spacing={1}
                                sx={{ mt: 2, clear: "both", float: "left" }}
                            >
                                <Avatar sx={{ bgcolor: colors.lco.neutral.main }}>
                                    <SmartToyIcon />
                                </Avatar>
                                <LoaderDots />
                            </Stack>
                        )}
                    </Box>

                    <Divider />

                    {/* Input Area */}
                    <Box
                        sx={{
                            p: 2,
                            bgcolor: colors.lco.white,
                        }}
                    >
                        <TextField
                            fullWidth
                            placeholder="Type your question..."
                            value={userInput}
                            onChange={handleUserInput}
                            onKeyDown={(e: any) => {
                                if (e.key === "Enter" && !e.shiftKey) {
                                    e.preventDefault();
                                    sendMessage();
                                }
                            }}
                            disabled={typingIndicator}
                            sx={{
                                "& .MuiOutlinedInput-root": {
                                    borderRadius: "24px",
                                    backgroundColor: "#f8f9fa",
                                },
                            }}
                            InputProps={{
                                endAdornment: (
                                    <IconButton
                                        onClick={sendMessage}
                                        disabled={!userInput.trim() || typingIndicator}
                                        sx={{ color: colors.lco.primary.main }}
                                    >
                                        <SendIcon />
                                    </IconButton>
                                ),
                            }}
                        />
                    </Box>
                </Paper>
            </ChatWrapper>

            {!isChatOpen && (
                <FloatButton onClick={() => setIsChatOpen(true)}>
                    <ChatIcon sx={{ mr: 1, fontSize: "1.2rem" }} />
                    {CHATBOT_CONFIG.floatingIconName}
                </FloatButton>
            )}
        </>
    );
};
